import { useState, useEffect } from 'react';
import update from 'immutability-helper';
import axios from 'axios';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { checkUserAuth } from '../../../../utils/auth-utils';

const defaultCourseData = {
  name: '',
  description: '',
  type: 1,
  level: 1,
  isDraft: true,
  isPopular: false,
  duration: 0,
  tasksCount: 0,
  oldPrice: 0,
  price: 0,
  sections: [],
};

const useEditCoursePageState = (courseId, history) => {
  const { token } = useSelector(state => state);

  const [courseData, setCourseData] = useState(defaultCourseData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const isEditMode = !_.isUndefined(courseId);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    setIsLoading(true);
    setIsError(false);
    axios
      .get(`/api/v1/courses/${courseId}`)
      .then(response => {
        setCourseData(response.data.course);
        setIsLoading(false);
        setIsError(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });
  }, [isEditMode]);

  const changeStringValue = propName => e => {
    setCourseData(update(courseData, { [propName]: { $set: e.target.value } }));
  };

  const changeNumberValue = propName => e => {
    const newValue = _.toNumber(e.target.value);
    setCourseData(update(courseData, { [propName]: { $set: !_.isNaN(newValue) ? newValue : 0 } }));
  };

  const changeBoolValue = propName => e => {
    setCourseData(update(courseData, { [propName]: { $set: e.target.checked } }));
  };

  const addSection = () => {
    setCourseData(update(courseData, { sections: { $push: [{ name: '', webinars: [] }] } }));
  };

  const removeSection = sectionIndex => () => {
    setCourseData(update(courseData, { sections: { $splice: [[sectionIndex, 1]] } }));
  };

  const changeSectionName = index => e => {
    setCourseData(update(courseData, { sections: { [index]: { name: { $set: e.target.value } } } }));
  };

  const addWebinar = sectionIndex => () => {
    setCourseData(
      update(courseData, { sections: { [sectionIndex]: { webinars: { $push: [{ name: '', paragraphs: [] }] } } } }),
    );
  };

  const removeWebinar = (sectionIndex, webinarIndex) => () => {
    setCourseData(update(courseData, { sections: { [sectionIndex]: { webinars: { $splice: [[webinarIndex, 1]] } } } }));
  };

  const changeWebinarName = (sectionIndex, webinarIndex) => e => {
    setCourseData(
      update(courseData, {
        sections: { [sectionIndex]: { webinars: { [webinarIndex]: { name: { $set: e.target.value } } } } },
      }),
    );
  };

  const addWebinarParagraph = (sectionIndex, webinarIndex) => () => {
    setCourseData(
      update(courseData, {
        sections: { [sectionIndex]: { webinars: { [webinarIndex]: { paragraphs: { $push: [''] } } } } },
      }),
    );
  };

  const removeWebinarParagraph = (sectionIndex, webinarIndex, paragraphIndex) => () => {
    setCourseData(
      update(courseData, {
        sections: {
          [sectionIndex]: { webinars: { [webinarIndex]: { paragraphs: { $splice: [[paragraphIndex, 1]] } } } },
        },
      }),
    );
  };

  const changeWebinarParagraph = (sectionIndex, webinarIndex, paragraphIndex) => e => {
    setCourseData(
      update(courseData, {
        sections: {
          [sectionIndex]: {
            webinars: { [webinarIndex]: { paragraphs: { [paragraphIndex]: { $set: e.target.value } } } },
          },
        },
      }),
    );
  };

  const saveData = () => {
    setIsLoading(true);
    setIsError(false);

    if (isEditMode) {
      axios
        .put(`/api/v1/courses/${courseId}`, courseData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(() => {
          setIsLoading(false);
          setIsError(false);
          history.push('/admin/courses');
        })
        .catch(e => {
          setIsLoading(false);
          setIsError(true);
          checkUserAuth(e, history);
        });
    } else {
      axios
        .post('/api/v1/courses', courseData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(() => {
          setIsLoading(false);
          setIsError(false);
          history.push('/admin/courses');
        })
        .catch(e => {
          setIsLoading(false);
          setIsError(true);
          checkUserAuth(e, history);
        });
    }
  };

  return {
    isEditMode,
    isLoading,
    isError,
    courseData,
    changeStringValue,
    changeNumberValue,
    changeBoolValue,
    addSection,
    removeSection,
    changeSectionName,
    addWebinar,
    removeWebinar,
    addWebinarParagraph,
    removeWebinarParagraph,
    changeWebinarName,
    changeWebinarParagraph,
    saveData,
  };
};

export default useEditCoursePageState;
